import React from 'react';
import styled, { css } from 'styled-components';
import { mapLegStatus } from '../../lib/bet-status';
import { removePipes } from '../../lib';
import { ILeg, IPriceInput } from '../../types';
import dayjs from 'dayjs';

import { formatDate } from '../../lib/time';
import { formatHandicap } from '../../lib';
import { Block, Text, robotoFonts } from '../typography';
import { BetDetailsVariant, sportsIconsByResultType, SportsResultIconProps } from '.';

export interface IProps {
  getLocalDateTime(dateTime: Date): Date;
  gridTemplateColumns?: string;
  isCompetitionSportVisible?: boolean;
  isEventStartTimeVisible?: boolean;
  legs: Leg[];
  paddingHorizontal?: string;
  type: string;
  variant: BetDetailsVariant;
}

export type Leg = {
  competition: Pick<ILeg['competition'], 'name'>;
  event: Pick<ILeg['event'], 'name'>;
  market: Pick<ILeg['market'], 'name'>;
  selection: Pick<ILeg['selection'], 'name'>;
  sport: Pick<ILeg['sport'], 'name'>;
  result: Parameters<typeof mapLegStatus>[0];
} & Pick<ILeg, 'eventStartTime' | 'marketLine' | 'id' | 'price' | 'originalPrice' | 'type' | 'priceType'>;

const formatSelection = (name: string, handicap: string) => `${name} ${handicap}`;

function abbreviateResultLabel({
  label,
  isInjuryProtected,
  variant
}: {
  label: string;
  isInjuryProtected?: boolean;
  variant?: BetDetailsVariant;
}): string {
  if (isInjuryProtected && variant === 'compact') {
    return label
      .split(' ')
      .map(word => word.charAt(0).toUpperCase())
      .join('');
  }
  return label;
}

const renderLeg = ({
  getLocalDateTime,
  isCompetitionSportVisible,
  isEventStartTimeVisible,
  legs,
  paddingHorizontal,
  type,
  variant
}: Required<
  Pick<
    IProps,
    | 'getLocalDateTime'
    | 'isCompetitionSportVisible'
    | 'isEventStartTimeVisible'
    | 'legs'
    | 'paddingHorizontal'
    | 'type'
    | 'variant'
  >
>) => {
  return legs.map((leg: Leg, i: number) => {
    const { selection, market, event, price, result, eventStartTime, competition, sport } = leg;
    const { label } = mapLegStatus(result);
    const name = removePipes(selection.name);

    const isVoided = result?.type === 'void';
    const isInjuryProtected = result?.type === 'injury_protection';
    const isStruckThrough = isVoided || isInjuryProtected;

    return (
      <BetLegsBlockRow
        className={`qa-bet-finder-leg qa-bet-finder-leg-${i}`}
        key={`bet-finder-leg-${i}`}
        type={result?.type}
        {...{ paddingHorizontal }}>
        {renderSelectionsCell({ name, leg, isStruckThrough })}
        {renderMarketCell({ market, isStruckThrough })}
        {renderEventNameCell({ event, isEventStartTimeVisible, getLocalDateTime, eventStartTime, isStruckThrough })}
        {isCompetitionSportVisible && renderCompetitionSportCell({ competition, sport, isStruckThrough })}
        {renderOddsCell({ type, price, isVoided: isStruckThrough })}
        {renderResultCellIcon({
          label,
          icon: sportsIconsByResultType[result?.type ?? ''],
          isInjuryProtected,
          isSGPBetLeg: false,
          variant
        })}
      </BetLegsBlockRow>
    );
  });
};

export function renderSelectionsCell({
  name,
  leg,
  isStruckThrough
}: {
  name: string;
  leg: Leg;
  isStruckThrough: boolean;
}) {
  return (
    <StrikableBlock
      size="md"
      className="qa-bet-finder-cell qa-bet-finder-leg-selection"
      isStruckThrough={isStruckThrough}>
      {formatSelection(name, formatHandicap(leg.marketLine, name, false))}
    </StrikableBlock>
  );
}

export function renderMarketCell({ market, isStruckThrough }: { market: { name: string }; isStruckThrough: boolean }) {
  return (
    <StrikableBlock size="md" className="qa-bet-finder-cell qa-bet-finder-leg-market" isStruckThrough={isStruckThrough}>
      {removePipes(market.name)}
    </StrikableBlock>
  );
}

export function renderEventNameCell({
  event,
  isEventStartTimeVisible,
  getLocalDateTime,
  eventStartTime,
  isStruckThrough
}: {
  event: { name: string };
  isEventStartTimeVisible: boolean;
  getLocalDateTime: (dateTime: Date) => Date;
  eventStartTime: string;
  isStruckThrough: boolean;
}) {
  return (
    <StrikableBlock size="md" className="qa-bet-finder-cell qa-bet-finder-leg-event" isStruckThrough={isStruckThrough}>
      {removePipes(event.name)}
      {isEventStartTimeVisible && (
        <>
          {' - '}
          <Text className="qa-bet-finder-cell qa-bet-finder-leg-date-time" weight={300}>
            {formatDate(getLocalDateTime(dayjs(eventStartTime).toDate()))}
          </Text>
        </>
      )}
    </StrikableBlock>
  );
}

export function renderCompetitionSportCell({
  competition,
  sport,
  isStruckThrough
}: {
  competition: { name: string };
  sport: { name: string };
  isStruckThrough: boolean;
}) {
  return (
    <StrikableBlock size="md" className="qa-bet-finder-cell qa-bet-finder-leg-market" isStruckThrough={isStruckThrough}>
      {`${competition.name} - ${removePipes(sport.name)}`}
    </StrikableBlock>
  );
}

export function renderOddsCell({
  price,
  type,
  isVoided
}: {
  price: IPriceInput | null;
  type: string;
  isVoided?: boolean;
}) {
  return (
    <PriceBlock size="md" className="qa-bet-finder-cell qa-bet-finder-leg-odds" isVoided={isVoided}>
      {type === 'BYO' || type.startsWith('PC') || !price ? '' : price.a}
    </PriceBlock>
  );
}

export function renderResultCellIcon({
  label,
  icon: Icon,
  isInjuryProtected,
  isSGPBetLeg,
  variant
}: {
  label: string;
  icon?: SportsResultIconProps;
  isInjuryProtected?: boolean;
  isSGPBetLeg?: boolean;
  variant?: BetDetailsVariant;
}) {
  return (
    <ResultCellWrapper
      className="qa-bet-finder-cell qa-bet-finder-leg-result"
      compact={isSGPBetLeg || variant === 'compact'}>
      {typeof Icon === 'function' ? (
        <Icon width={isSGPBetLeg ? '1.5rem' : '2rem'} />
      ) : (
        <ResultIcon icon={Icon} isSGPBetLeg={isSGPBetLeg} />
      )}
      <span>{abbreviateResultLabel({ label, variant, isInjuryProtected })}</span>
    </ResultCellWrapper>
  );
}

const ResultCellWrapper = styled(Block).attrs({
  size: 'md'
})<{ compact?: boolean }>`
  display: flex;
  align-items: center;
  gap: ${({ compact }) => (compact ? '0.35rem' : '0.85rem')};
`;

export default function BetLegs({
  getLocalDateTime,
  gridTemplateColumns,
  isCompetitionSportVisible = true,
  isEventStartTimeVisible = true,
  legs,
  paddingHorizontal = '2.4rem',
  type,
  variant
}: IProps) {
  return (
    <BetLegsScrollContainer>
      <BetLegsBlock className="qa-bet-finder-bet-legs-table" {...{ gridTemplateColumns }}>
        <BetLegsBlockHead className="qa-bet-finder-header" {...{ paddingHorizontal }}>
          {renderHeaderRow(legs, isCompetitionSportVisible)}
        </BetLegsBlockHead>
        {renderLeg({
          getLocalDateTime,
          isCompetitionSportVisible,
          isEventStartTimeVisible,
          legs,
          paddingHorizontal,
          type,
          variant
        })}
      </BetLegsBlock>
    </BetLegsScrollContainer>
  );
}

export const renderHeaderRow = (legs: Leg[], isCompetitionSportVisible: boolean) => (
  <>
    <Block size="md" className="qa-bet-finder-cell">
      <span>Selections</span>
      &nbsp;{`(${legs.length})`}
    </Block>
    <Block size="md" className="qa-bet-finder-cell">
      <span>Market</span>
    </Block>
    <Block size="md" className="qa-bet-finder-cell">
      <span>Event Name</span>
    </Block>
    {isCompetitionSportVisible && (
      <Block size="md" className="qa-bet-finder-cell">
        <span>Competition - Sport</span>
      </Block>
    )}
    <Block size="md" className="qa-bet-finder-cell">
      <span>Odds</span>
    </Block>
    <Block size="md" className="qa-bet-finder-cell">
      <span>Result</span>
    </Block>
  </>
);

export const BetLegsScrollContainer = styled.div`
  height: 100%;
  overflow-y: auto;
  &::-webkit-scrollbar-track {
    background-color: ${({ theme }) => theme.colors.white};
  }
  &::-webkit-scrollbar {
    display: inline;
    width: 0.5rem;
    background-color: ${({ theme }) => theme.colors.grey100};
  }
  &::-webkit-scrollbar-thumb {
    border-radius: ${({ theme }) => theme.borderRadius.xlarge};
    background-color: ${({ theme }) => theme.colors.grey300};
  }
`;

export const BetLegsBlock: any = styled.div<{ gridTemplateColumns: string; height: string }>`
  background-color: ${({ theme }) => theme.colors.white};
  display: grid;
  grid-template-columns: ${({ gridTemplateColumns }) => gridTemplateColumns || '7fr 5fr 15fr 15fr 3fr 7fr'};
  text-align: left;
`;

const BetLegsBlockRow = styled.div<{ paddingHorizontal: string; type?: string }>`
  display: contents;
  & > div {
    display: flex;
    font-weight: 400;
    justify-content: flex-start;
    padding: 1.2rem 0;

    ${({ theme, type }) => {
      if (type === 'void') {
        return css`
          background-color: ${theme.colors.status.alert};
        `;
      }
      if (type === 'injury_protection') {
        return css`
          color: ${theme.colors.white};
          background-color: ${theme.components.injuryProtection.row};
        `;
      }
      return '';
    }}
  }
  & > div:first-child {
    padding-left: ${({ paddingHorizontal }) => paddingHorizontal};
  }
  & > div:last-child {
    display: flex;
    justify-content: flex-end;
    padding-right: ${({ paddingHorizontal }) => paddingHorizontal};
    text-align: right;
  }
`;

export const BetLegsBlockHead = styled(BetLegsBlockRow)`
  display: contents;
  & > div {
    background-color: ${({ theme }) => theme.colors.supporting.accent100};
    border-top: 1px solid ${({ theme }) => theme.colors.supporting.accent300};
    font-weight: 700;
    position: sticky;
    top: 0;
    z-index: 1;
  }
`;

const struckThroughStyles = css`
  text-decoration: line-through;
  color: ${({ theme }) => theme.colors.neutral};
`;

const StrikableBlock = styled(Block)<{ isStruckThrough?: boolean }>`
  ${({ isStruckThrough }) => isStruckThrough && struckThroughStyles};
`;

const PriceBlock = styled(Block)<{ isVoided?: boolean }>`
  ${({ isVoided }) => isVoided && struckThroughStyles};
`;

export const ResultIcon = styled.span<{
  icon?: { color: string; content: string };
  isSGPBetLeg?: boolean;
}>`
  ${({ icon, theme, isSGPBetLeg = false }) =>
    icon &&
    css`
      display: flex;
      align-items: center;
      &:before {
        background-color: ${theme.colors.modals[icon.color]};
        color: ${theme.colors.white};
        /* prettier-ignore */
        content: "${icon.content}";
        padding: ${isSGPBetLeg ? '0.1rem' : '0.2rem'};
        position: relative;
        text-align: center;

        border-radius: 50%;
        font-size: ${isSGPBetLeg ? '1rem' : robotoFonts.sm};
        margin-left: 0.35rem;
        height: ${isSGPBetLeg ? '1.3rem' : '2rem'};
        width: ${isSGPBetLeg ? '1.3rem' : '2rem'};
      }
    `};
`;

export const BetLegsBlockStatusText = styled(Text)<{ type: any }>`
  position: relative;
  ${({ type }) =>
    type &&
    css`
      font-weight: normal;
      padding-left: 2rem;
      &:before {
        background-color: ${({ theme }) => theme.colors.status[type]};
        border-radius: 100%;
        content: '';
        display: block;
        height: 14px;
        left: 0;
        position: absolute;
        top: 4px;
        width: 14px;
      }
    `};
`;
